import { useEffect, useRef, type RefObject } from 'react';
import { FOCUSABLE_SELECTOR, trapFocusTarget } from './mobile.js';

/**
 * Modal behaviour of the mobile context bottom sheet (M3), "focus trap lite":
 * focus moves into the sheet when it opens and returns to the element that
 * opened it on close, Tab / Shift+Tab wrap inside it, Esc closes it and the
 * page behind does not scroll while it is open.
 */
export function useModalSheet(
  open: boolean,
  sheetRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const sheet = sheetRef.current;
    const opener = document.activeElement as HTMLElement | null;
    sheet?.focus();

    // Lock every scroll root the shell may use; restore exactly what was there.
    const roots = [document.documentElement, document.body];
    const previousOverflow = roots.map((root) => root.style.overflow);
    roots.forEach((root) => {
      root.style.overflow = 'hidden';
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !sheet) return;
      const focusable = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      const current = focusable.indexOf(document.activeElement as HTMLElement);
      const target = trapFocusTarget(current, focusable.length, event.shiftKey);
      if (target !== null) {
        event.preventDefault();
        focusable[target]?.focus();
      } else if (focusable.length === 0) {
        event.preventDefault();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      roots.forEach((root, index) => {
        root.style.overflow = previousOverflow[index] ?? '';
      });
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open, sheetRef]);
}
